/**
 * Dealer Terms Consent Service
 * Handles checking and saving dealer terms acceptance
 * GDPR Art. 7 - Consent verification and persistence for dealer operators
 */

import { supabase } from '@/lib/supabase/client';
import { logConsent } from './consentService';
import { debugLog, debugError } from '@/utils/debugLogger';

/** Current dealer terms version - update this to force re-acceptance */
export const DEALER_TERMS_VERSION = '2026-02-22';

/**
 * Check if a dealer has accepted the current version of dealer terms
 */
export async function checkDealerTermsConsent(userId: string): Promise<boolean> {
  try {
    debugLog('auth', `Checking dealer terms consent for user: ${userId}`, undefined, 'DealerTermsConsentService');

    const { data, error } = await supabase
      .from('user_preferences')
      .select('dealer_terms_accepted, dealer_terms_version')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      debugError('auth', 'Error checking dealer terms consent:', error, 'DealerTermsConsentService');
      return false;
    }

    const hasConsent =
      data?.dealer_terms_accepted === true &&
      data?.dealer_terms_version === DEALER_TERMS_VERSION;

    debugLog('auth', `Dealer terms consent: ${hasConsent ? 'valid' : 'required'}`, undefined, 'DealerTermsConsentService');
    return hasConsent;
  } catch (error) {
    debugError('auth', 'Exception checking dealer terms consent:', error, 'DealerTermsConsentService');
    return false;
  }
}

/**
 * Save dealer terms acceptance in user_preferences and log to consent_logs
 */
export async function saveDealerTermsConsent(userId: string): Promise<boolean> {
  try {
    debugLog('auth', `Saving dealer terms consent for user: ${userId}`, undefined, 'DealerTermsConsentService');

    const now = new Date().toISOString();

    // 1. Upsert user_preferences
    const { error: prefsError } = await supabase
      .from('user_preferences')
      .upsert(
        {
          user_id: userId,
          dealer_terms_accepted: true,
          dealer_terms_accepted_at: now,
          dealer_terms_version: DEALER_TERMS_VERSION,
        },
        { onConflict: 'user_id' }
      );

    if (prefsError) {
      debugError('auth', 'Failed to upsert dealer terms in user_preferences:', prefsError, 'DealerTermsConsentService');
    }

    // 2. Log DEALER_TERMS consent for GDPR audit
    const logSuccess = await logConsent({
      consentType: 'DEALER_TERMS',
      granted: true,
      source: 'dealer_terms_consent_dialog',
      userId,
    });

    if (!logSuccess) {
      debugError('auth', 'Failed to log DEALER_TERMS consent', undefined, 'DealerTermsConsentService');
    }

    const success = !prefsError && logSuccess;
    if (success) {
      debugLog('auth', 'Dealer terms consent saved successfully', undefined, 'DealerTermsConsentService');
    }

    return success;
  } catch (error) {
    debugError('auth', 'Exception saving dealer terms consent:', error, 'DealerTermsConsentService');
    return false;
  }
}
